import dayjs, { Dayjs } from 'dayjs';
import duration from 'dayjs/plugin/duration';

dayjs.extend(duration);

/**
 * Check if a string is an ISO 8601 duration (starts with P or -P)
 */
export const isDuration = (value: string): boolean => {
  if (typeof value !== 'string') return false;
  return /^-?P/.test(value.trim());
};

/**
 * Parse an ISO 8601 duration string and return a dayjs duration object.
 * Supports negative durations prefixed with '-' (e.g., '-P1D' for 1 day ago)
 */
export const parseDuration = (
  value: string,
): { duration: duration.Duration; negative: boolean } => {
  const trimmed = value.trim();
  const negative = trimmed.startsWith('-');
  const durationStr = negative ? trimmed.slice(1) : trimmed;
  return {
    duration: dayjs.duration(durationStr),
    negative,
  };
};

/**
 * Round a dayjs time to the nearest interval (in milliseconds).
 * @param time - The time to round
 * @param intervalMs - The interval in milliseconds to round to
 * @param mode - 'floor' (default), 'ceil', or 'round'
 */
export const roundTime = (
  time: Dayjs,
  intervalMs: number,
  mode: 'floor' | 'ceil' | 'round' = 'floor',
): Dayjs => {
  if (!intervalMs || intervalMs <= 0) return time;

  const ms = time.valueOf();
  let rounded: number;

  switch (mode) {
    case 'ceil':
      rounded = Math.ceil(ms / intervalMs) * intervalMs;
      break;
    case 'round':
      rounded = Math.round(ms / intervalMs) * intervalMs;
      break;
    case 'floor':
    default:
      rounded = Math.floor(ms / intervalMs) * intervalMs;
      break;
  }

  return dayjs(rounded);
};

/**
 * Parse a time value that can be either:
 * - An ISO 8601 date-time string (e.g., '2025-01-01T00:00:00Z')
 * - An ISO 8601 duration relative to now (e.g., 'PT0H', '-P1D', 'P7D')
 *
 * @param value - The time string to parse
 * @param reference - Reference time for duration calculations (default: now)
 * @param roundIntervalMs - Optional interval to round the result to (in milliseconds)
 */
export const parseTimeValue = (
  value: string,
  reference: Dayjs = dayjs(),
  roundIntervalMs?: number,
): Dayjs => {
  let result: Dayjs;

  if (isDuration(value)) {
    const { duration: dur, negative } = parseDuration(value);
    result = negative ? reference.subtract(dur) : reference.add(dur);
  } else {
    result = dayjs(value);
  }

  if (roundIntervalMs && roundIntervalMs > 0) {
    result = roundTime(result, roundIntervalMs);
  }

  return result;
};

/**
 * Parse a time range where each value can be a date-time or duration.
 *
 * @param range - Array of two time strings [start, end]
 * @param reference - Reference time for duration calculations (default: now)
 * @param roundIntervalMs - Optional interval to round both times to
 */
export const parseTimeRange = (
  range: [string, string],
  reference: Dayjs = dayjs(),
  roundIntervalMs?: number,
): [Dayjs, Dayjs] => {
  return [
    parseTimeValue(range[0], reference, roundIntervalMs),
    parseTimeValue(range[1], reference, roundIntervalMs),
  ];
};

/**
 * Parse increment value which can be:
 * - A number (seconds)
 * - An ISO 8601 duration string (e.g., 'PT1H', 'P1D')
 *
 * @returns Interval in milliseconds
 */
export const parseIncrement = (increment: number | string): number => {
  if (typeof increment === 'number') {
    return increment * 1000; // Convert seconds to milliseconds
  }
  if (typeof increment === 'string' && isDuration(increment)) {
    const { duration: dur } = parseDuration(increment);
    return dur.asMilliseconds();
  }
  return 0;
};
